<template>
    <Transition name="fade" mode="out-in">
        <div v-if="loading" class="flex flex-col gap-6">
            <MiscSkeleton width="100%" height="160px" :repeat="3" />
        </div>
        <div v-else-if="orders && orders.length > 0">
            <div v-for="order in orders" :key="order.id" class="mb-6">
                <CustomerOrder v-if="orderPaymentStatusNotFailed(order?.transactions?.[order?.transactions?.length - 1]?.stateMachineState?.technicalName)" :order="order" />
            </div>

            <div class="flex justify-center">
                <div class="btn-group flex-nowrap">
                    <button
                        :disabled="currentPage === 1"
                        type="button"
                        class="btn btn-sm"
                        :aria-label="$t('customer.order.firstPage')"
                        @click="changeCurrentPage(1)"
                    >
                        <MiscIcon name="chevrons-left" class="h-5 w-5" />
                    </button>
                    <button
                        :disabled="currentPage - 1 < 1"
                        type="button"
                        class="btn btn-sm"
                        :aria-label="$t('customer.order.prevPage')"
                        @click="changeCurrentPage(currentPage - 1)"
                    >
                        <MiscIcon name="chevron-left" class="h-5 w-5" />
                    </button>
                    <div class="btn btn-sm btn-ghost normal-case">
                        {{ `Page ${currentPage} of ${maxPage}` }}
                    </div>
                    <button
                        type="button"
                        class="btn btn-sm"
                        :disabled="currentPage + 1 > maxPage"
                        :aria-label="$t('customer.order.nextPage')"
                        @click="changeCurrentPage(currentPage + 1)"
                    >
                        <MiscIcon name="chevron-right" class="h-5 w-5" />
                    </button>
                    <button
                        :disabled="currentPage + 1 > maxPage"
                        type="button"
                        class="btn btn-sm"
                        :aria-label="$t('customer.order.lastPage')"
                        @click="changeCurrentPage(maxPage)"
                    >
                        <MiscIcon name="chevrons-right" class="h-5 w-5" />
                    </button>
                </div>
            </div>
        </div>
        <div v-else class="p-4 lg:p-0">
            {{ $t('customer.order.noOrders') }}
        </div>
    </Transition>
</template>

<script setup lang="ts">
import { ApiClientError } from '@shopware/api-client'

const {
    loadOrders,
    orders,
    limit,
    totalPages,
    currentPage,
    changeCurrentPage
} = useCustomerOrders()

const loading = ref(true)

const localePath = useLocalePath()
const $config = useRuntimeConfig()
const basePath = ($config?.app?.baseURL || '/') === '/' ? '' : ($config?.app?.baseURL || '')

const maxPage = computed(() => {
    return Math.ceil(totalPages.value / limit.value)
})

function orderPaymentStatusNotFailed (status: string | undefined) {
    return status != 'failed' && status != 'cancelled'
}

onMounted(async () => {
    try {
        await loadOrders({
            associations: {
                deliveries: {
                    associations: {
                        shippingMethod: {
                            associations: {
                                prices: {}
                            }
                        },
                        shippingOrderAddress: {
                            associations: {
                                salutation: {}
                            }
                        },
                        stateMachineState: {}
                    }
                },
                transactions: {
                    associations: {
                        paymentMethod: {},
                        stateMachineState: {}
                    }
                },
                lineItems: {
                    associations: {
                        cover: {},
                        downloads: {
                            associations: {
                                media: {}
                            }
                        }
                    }
                },
                billingAddress: {
                    associations: {
                        salutation: {}
                    }
                },
                stateMachineState: {}
            },
            sort: [
                {
                    field: 'createdAt',
                    order: 'DESC',
                    naturalSorting: true
                }
            ],
            filter: [
                {
                    type: 'multi',
                    operator: 'or',
                    queries: [
                        {
                            type: 'equals',
                            field: 'transactions.stateMachineState.technicalName',
                            value: 'authorized'
                        },
                        {
                            type: 'equals',
                            field: 'transactions.stateMachineState.technicalName',
                            value: 'paid'
                        },
                        {
                            type: 'equals',
                            field: 'transactions.stateMachineState.technicalName',
                            value: 'refunded'
                        },
                        {
                            type: 'equals',
                            field: 'transactions.stateMachineState.technicalName',
                            value: 'refunded_partially'
                        }
                    ]
                }
            ]
        })
    } catch (e: unknown) {
        if (e instanceof ApiClientError && e.status === 403) {
            window.location.href = `${basePath}${localePath('/customer/login')}?returnUrl=/customer/orders`
            return
        }
    } finally {
        loading.value = false
    }
})
</script>
